import { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Wrench } from "lucide-react";

const NEXT = {
  pending: ["in_progress", "blocked", "completed"],
  in_progress: ["blocked", "completed"],
  blocked: ["in_progress"],
  completed: [],
};

export default function FieldDashboard() {
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [target, setTarget] = useState("");

  const load = () => api.get("/maintenance").then((r) => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const apply = async (t, status, reason) => {
    try {
      const body = { status };
      if (reason) body.blocked_reason = reason;
      await api.patch(`/maintenance/${t.id}`, body);
      toast.success(`Task ${status.replace("_", " ")}`);
      setActive(null); setBlockReason(""); setTarget("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleClick = (t, s) => {
    if (s === "blocked") { setActive(t); setTarget(s); return; }
    apply(t, s);
  };

  const groups = { in_progress: [], pending: [], blocked: [], completed: [] };
  tasks.forEach((t) => { (groups[t.status] || groups.pending).push(t); });

  return (
    <div className="p-6 md:p-10 max-w-3xl pb-24">
      <div className="flex items-center gap-3 mb-1">
        <Wrench className="text-primary" />
        <h1 className="heading text-3xl md:text-4xl">My tasks</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-8">Tap a task to update its status.</p>

      {["in_progress", "pending", "blocked", "completed"].map((k) =>
        groups[k].length > 0 && (
          <section key={k} className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">{k.replace("_", " ")}</div>
            <div className="space-y-3">
              {groups[k].map((t) => (
                <div key={t.id} className="border border-border rounded-lg bg-card p-4" data-testid={`task-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{t.title}</div>
                      {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
                      <div className="text-[10px] uppercase tracking-widest mt-2 text-muted-foreground">Priority · {t.priority}</div>
                    </div>
                    <span className={`status-pill status-${t.status}`}>{t.status.replace("_", " ")}</span>
                  </div>
                  {NEXT[t.status]?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {NEXT[t.status].map((s) => (
                        <Button key={s} size="sm" variant={s === "completed" ? "default" : "outline"} onClick={() => handleClick(t, s)} data-testid={`task-${t.id}-to-${s}`}>
                          Mark {s.replace("_", " ")}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )
      )}
      {tasks.length === 0 && <div className="text-sm text-muted-foreground">No tasks assigned.</div>}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Why is this blocked?</DialogTitle></DialogHeader>
          <Textarea placeholder="e.g. Waiting for parts, weather delay" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} data-testid="block-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button disabled={!blockReason.trim()} onClick={() => apply(active, target, blockReason.trim())} data-testid="block-confirm-btn">Confirm block</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
